import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { RootState } from '../store/store';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_URL || 'https://api.sama.com/v1',
  prepareHeaders: (headers, { getState, endpoint }) => {
    const token = (getState() as RootState).auth.token;
    const partnerId = (getState() as RootState).auth.partner?.id;
    const activeStoreId = (getState() as RootState).auth.activeStoreId;

    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    if (partnerId) {
      headers.set('x-partner-id', partnerId);
    }

    const globalEndpoints = new Set([
      'getTenantUsers',
      'registerTenantUser',
      'updateTenantUser',
      'assignTenantUserRole',
      'deleteTenantUser',
      'getTenantRoles',
      'getAvailablePermissions',
      'createTenantRole',
      'updateTenantRole',
      'deleteTenantRole',
      'getPartnerLocations',
      'createPartnerLocation',
      'updatePartnerLocation',
      'deletePartnerLocation',
      'getPartnerMe',
      'updatePartnerMe',
      'getExchangeRates',
      'getExchangeRateById',
      'createExchangeRate',
      'updateExchangeRate',
      'getProducts',
      // offers son a nivel partner, no por sucursal — no deben recibir x-partner-location-id
      'getOffers',
      'getOfferById',
      'createOffer',
      'updateOffer',
      'deleteOffer',
      // combos (promos) también son a nivel partner; la sucursal va en body/query
      'getCombos',
      'getComboById',
      'createCombo',
      'updateCombo',
      'deleteCombo',
      // Admin order endpoints (cross-partner, no store scoping)
      'getAdminOrders',
      'getAdminOrderById',
      'deleteAdminOrder',
      'verifyAdminOrder',
      // Reportes del partner: la sucursal se elige EN la vista y viaja como
      // query param (?partnerLocationId). Sin header, "todas las sucursales"
      // es expresable; con header, el backend siempre filtraría por la activa.
      'getReportOverview',
      'getReportTimeseries',
      'getReportCatalog',
      // Cobranza de comisiones: la deuda del partner con SAMA es de todo el
      // partner, no de una sucursal. Con x-partner-location-id el backend
      // devolvería solo una parte del payout y el reporte de pago no cuadraría.
      'uploadPayoutProof',
      // Facturas neteadas: el neto del partner es de todo el partner, no de
      // una sucursal. Con x-partner-location-id el backend devolvería un neto
      // parcial y el facturado divergiría del portal. Nombres exactos de los
      // endpoints de Fase 1 (admin + partner); un typo reactiva el bug.
      'getAdminPartnerInvoices',
      'getAdminPartnerInvoiceById',
      'runInvoiceCycle',
      'issuePartnerInvoice',
      'approveInvoicePayouts',
      'voidPartnerInvoice',
      'resolveInvoiceDispute',
      'cancelInvoicePayout',
      'getPartnerInvoices',
      'getPartnerInvoiceById',
      'getPartnerInvoiceCurrent',
      'reportPartnerInvoicePayment',
      'disputePartnerInvoice',
      // Ajustes del storefront (modo de precios + logo): son del partner, no de una sucursal.
      'getPartnerSettings',
      'updatePartnerSettings',
      'uploadPartnerLogo',
      'deletePartnerLogo',
      // Cuenta propia y suscripción: son del usuario/partner, no de una sucursal.
      'getMyAccount',
      'updateMyAccount',
      'changeMyPassword',
      'getMySubscription',
      'getMySubscriptionInvoices',
    ]);

    const storefrontEndpoints = new Set([
      'getStorefrontConfig',
      'getStorefrontProducts',
      'getStorefrontProductDetail',
      'getStorefrontCombos',
      'checkLocationCoverage',
      'calculateOrderBudget',
      'validateOrderBudget',
      'requestIncomingPaymentTicket',
      'requestOrderVerificationTicket',
      'requestDebitOtp',
      'getStorefrontBanks',
      'uploadPaymentProof',
      'registerOrderPushSubscription',
    ]);

    const isGlobal = endpoint && globalEndpoints.has(endpoint);
    const isStorefront = endpoint && storefrontEndpoints.has(endpoint);

    if (activeStoreId && !isGlobal && !isStorefront) {
      if (!headers.has('x-partner-location-id')) {
        headers.set('x-partner-location-id', activeStoreId);
      }
    }
    return headers;
  },
});

const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    const url = typeof args === 'string' ? args : args.url;
    if (!url?.includes('/auth/login')) {
      api.dispatch({ type: 'auth/logout' });
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }
  return result;
};

export const baseApi = createApi({
  reducerPath: 'baseApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Product',
    'Order',
    'Modifier',
    'AdminPartnerLocation',
    'PartnerLocation',
    'Category',
    'Finance',
    'Partner',
    'Payment',
    'Offer',
    'Report',
    'Payout',
    'DeliveryProvider',
    'User',
    'Role',
    'Settlement',
    'DeliveryIntegration',
    'CustomerInformation',
    'StorefrontConfig',
    'OrderPayment',
    'SubscriptionPlan',
    'Subscription',
    'DeliveryPayoutAccount',
    'DeliverySettlement',
    'DeliveryPayout',
    'AdminOrder',
    'PartnerCollectSettlement',
    'Bank',
    'PartnerInvoice',
    'Invoice',
    'Industry',
    'PartnerSettings',
    'Combo',
    'PaymentMethod',
  ],
  keepUnusedDataFor: 5 * 60,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  refetchOnMountOrArgChange: false,
  endpoints: () => ({}),
});
